import React from "react";
import {
  Box,
  Flex,
  Text,
  Badge,
  Button,
  useColorModeValue,
  Icon,
  Tooltip,
  VStack,
  HStack,
  Divider,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import useAxios from "axios-hooks";
import { useSetRecoilState } from "recoil";
import {
  FaCalendarAlt,
  FaMoneyBillWave,
  FaCheckCircle,
  FaClock,
  FaPlay,
  FaTimes,
  FaTrash,
  FaChartLine,
  FaExclamationTriangle,
} from "react-icons/fa";

import { toastState } from "atoms/toastState";
import logger from "infra/logger";

// Motion components
const MotionBox = motion(Box);

// Glassmorphism Card Component
const GlassMorphismCard = ({ children, ...props }) => {
  const cardBg = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.2)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <MotionBox
      bg={cardBg}
      backdropFilter="blur(20px)"
      borderRadius="16px"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.37)"
      p={6}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -2, boxShadow: "0 12px 40px 0 rgba(31, 38, 135, 0.45)" }}
      {...props}
    >
      {children}
    </MotionBox>
  );
};

const PayrunBox = ({ payrun, fetchPayrun }) => {  const textColor = useColorModeValue("gray.700", "white");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const setToast = useSetRecoilState(toastState);

  const [
    { loading: actionLoading },
    executeAction,
  ] = useAxios({}, { manual: true });

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      month: date.toLocaleString("en-US", { month: "short" }).toUpperCase(),
      year: date.getFullYear(),
      fullDate: date.toLocaleDateString("en-IN"),
    };
  };

  const { month, year, fullDate } = formatDate(payrun?.Date);

  // Status configuration
  const getStatusConfig = (status) => {
    switch (status) {
      case "yet_to_be_approved":
        return {
          label: "Pending Approval",
          colorScheme: "brand",
          icon: FaClock,
          bg: "rgba(255, 179, 0, 0.1)",
          color: "brand.500",
        };
      case "approved":
        return {
          label: "Approved",
          colorScheme: "green",
          icon: FaCheckCircle,
          bg: "rgba(72, 187, 120, 0.1)",
          color: "green.500",
        };
      case "payment_initiated":
        return {
          label: "Payment Initiated",
          colorScheme: "blue",
          icon: FaPlay,
          bg: "rgba(66, 153, 225, 0.1)",
          color: "blue.500",
        };
      case "payment_completed":
        return {
          label: "Payment Completed",
          colorScheme: "purple",
          icon: FaChartLine,
          bg: "rgba(128, 90, 213, 0.1)",
          color: "purple.500",
        };
      default:
        return {
          label: "Unknown",
          colorScheme: "gray",
          icon: FaExclamationTriangle,
          bg: "rgba(128, 128, 128, 0.1)",
          color: "gray.500",
        };
    }
  };

  const statusConfig = getStatusConfig(payrun?.PaymentStatus);

  // Action handlers
  const handleAction = async (action) => {
    try {
      const response = await executeAction({
        method: "PATCH",
        url: `api/payrun/update/${payrun?.Id}?status=${action}`,
      });

      setToast({
        isOpen: true,
        title: "Success",
        status: "success",
        description: response?.data?.message || "Action completed successfully",
      });

      if (fetchPayrun) fetchPayrun();
    } catch (error) {
      logger.log(error);
      if (error?.code !== "ERR_CANCELED") {
        setToast({
          isOpen: true,
          title: "Error Occurred",
          status: "error",
          description: error?.response?.data?.error || "An error occurred",
        });
      }
    }
  };
  const handleDeleteClick = async () => {
    try {
      await executeAction({
        method: "DELETE",
        url: `api/payrun/delete/${payrun?.Id}`,
      });

      setToast({
        isOpen: true,
        title: "Success",
        status: "success",
        description: "Payout run deleted successfully",
      });

      if (fetchPayrun) fetchPayrun();
    } catch (error) {
      setToast({
        isOpen: true,
        title: "Error Occurred",
        status: "error",
        description: error?.response?.data?.error || "Failed to delete payout run",
      });
    }
  };

  // Render action buttons based on status
  const renderActionButtons = () => {
    const { PaymentStatus } = payrun;

    return (
      <HStack spacing={2} flex={1} justify="flex-end">
        {(PaymentStatus === "yet_to_be_approved" || PaymentStatus === "approved") && (
          <Tooltip 
            label={PaymentStatus === "approved" ? "Initiate Payment" : "Approve Payout Run"} 
            hasArrow
          >
            <Button
              leftIcon={<Icon as={PaymentStatus === "approved" ? FaPlay : FaCheckCircle} />}
              size="sm"
              colorScheme={PaymentStatus === "approved" ? "blue" : "green"}
              variant="ghost"
              isLoading={actionLoading}
              onClick={() => 
                handleAction(PaymentStatus === "approved" ? "initiate_payment" : "approve")
              }
              _hover={{
                bg: PaymentStatus === "approved" ? "blue.50" : "green.50",
                transform: "translateY(-1px)",
              }}
            >
              {PaymentStatus === "approved" ? "Initiate" : "Approve"}
            </Button>
          </Tooltip>
        )}

        {PaymentStatus === "approved" && (
          <Tooltip label="Unapprove Payout Run" hasArrow>
            <Button
              leftIcon={<Icon as={FaTimes} />}
              size="sm"
              colorScheme="brand"
              variant="ghost"
              isLoading={actionLoading}
              onClick={() => handleAction("unapprove")}
              _hover={{
                bg: "brand.50",
                transform: "translateY(-1px)",
              }}
            >
              Unapprove
            </Button>
          </Tooltip>
        )}

        {PaymentStatus === "yet_to_be_approved" && (
          <Tooltip label="Delete Payout Run" hasArrow>
            <Button
              leftIcon={<Icon as={FaTrash} />}
              size="sm"
              colorScheme="red"
              variant="ghost"
              isLoading={actionLoading}
              onClick={handleDeleteClick}
              _hover={{
                bg: "red.50",
                transform: "translateY(-1px)",
              }}
            >
              Delete
            </Button>
          </Tooltip>
        )}

        {PaymentStatus === "payment_initiated" && (
          <Badge
            colorScheme="blue"
            variant="subtle"
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
            leftIcon={<Icon as={FaPlay} />}
          >
            Payment in Progress
          </Badge>
        )}

        {PaymentStatus === "payment_completed" && (
          <Badge
            colorScheme="purple"
            variant="subtle"
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
            leftIcon={<Icon as={FaChartLine} />}
          >
            Completed
          </Badge>
        )}
      </HStack>
    );
  };

  return (    <Box width="100%" display="flex" alignItems="center" justifyContent="center">
      <GlassMorphismCard
        w="100%"
        maxW="none"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.3 }}
      >
        <VStack spacing={4} align="stretch">
          {/* Header with Date and Status */}
          <Flex justify="space-between" align="center">
            <HStack spacing={3}>
              <Box
                bg={statusConfig.bg}
                color={statusConfig.color}
                p={3}
                borderRadius="lg"
              >
                <Icon as={FaCalendarAlt} w={6} h={6} />
              </Box>
              <VStack spacing={0} align="start">
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  {month} {year}
                </Text>
                <Text fontSize="sm" color={mutedColor}>
                  {fullDate}
                </Text>
              </VStack>
            </HStack>

            <Badge
              colorScheme={statusConfig.colorScheme}
              variant="subtle"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
              leftIcon={<Icon as={statusConfig.icon} />}
            >
              {statusConfig.label}
            </Badge>
          </Flex>

          <Divider />

          {/* Amount and Actions */}
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Box
                bg="rgba(72, 187, 120, 0.1)"
                color="green.500"
                p={3}
                borderRadius="lg"
              >
                <Icon as={FaMoneyBillWave} w={6} h={6} />
              </Box>
              <VStack spacing={0} align="start">
                <Text fontSize="3xl" fontWeight="bold" color={textColor}>
                  ₹{payrun?.TotalAmount?.toLocaleString('en-IN', { 
                    minimumFractionDigits: 2 
                  })}
                </Text>
                <Text fontSize="sm" color={mutedColor}>
                  Total Payout Amount
                </Text>
              </VStack>
            </HStack>

            {renderActionButtons()}
          </Flex>        </VStack>
      </GlassMorphismCard>
    </Box>
  );
};

export default PayrunBox;
